/**
 * SK formátovanie peňazí, dátumov, čísel — konzistentne s PDF
 * exportom (backend/src/tandem_budget/exporters/pdf_offer.py).
 *
 * Format peniaza: "1 234,56 €" (medzera = tisícky, čiarka = desat).
 * Format dátumu: "DD.MM.YYYY".
 */

export function formatEur(value: string | number | null | undefined): string {
  if (value === null || value === undefined || value === "") return "—";
  const num = typeof value === "number" ? value : Number(value);
  if (Number.isNaN(num)) return "—";
  return (
    num
      .toLocaleString("sk-SK", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
      // sk-SK locale niekedy používa NBSP ( ) — zachovať
      + " €"
  );
}

export function formatMm(value: string | number | null | undefined): string {
  if (value === null || value === undefined || value === "") return "—";
  const num = typeof value === "number" ? value : Number(value);
  if (Number.isNaN(num)) return "—";
  return num.toLocaleString("sk-SK", { maximumFractionDigits: 1 });
}

/**
 * Rozmer v mm po slovensky bez zaokrúhlenia na desatiny — `0.45` → `0,45`,
 * `19.00` → `19`, `2800` → `2800` (bez medzery tisícok, rozmer nie je
 * suma). `formatMm` zaokrúhľuje na desatiny, hrúbka hrany by vyšla 0,5.
 */
export function cisloMm(value: string | number | null | undefined): string {
  if (value === null || value === undefined || value === "") return "—";
  const num = toNum(value);
  if (!Number.isFinite(num)) return "—";
  return num.toLocaleString("sk-SK", { maximumFractionDigits: 2, useGrouping: false });
}

/** Šírka × hrúbka hrany — `22×0,45`; chýbajúca hodnota `?`. */
export function rozmerHrany(
  sirka: string | number | null | undefined,
  hrubka: string | number | null | undefined,
  oddelovac = "×",
): string {
  const s = cisloMm(sirka);
  const h = cisloMm(hrubka);
  return `${s === "—" ? "?" : s}${oddelovac}${h === "—" ? "?" : h}`;
}

/**
 * Stĺpec Rozmer vo výbere z cenníka (KOLO 201) — doska hrúbkou, hrana
 * šírkou × hrúbkou, tyč či tabuľa formátom; inak `—`.
 */
export function rozmerPolozky(m: {
  z_mm?: string | number | null;
  sirka_mm?: string | number | null;
  hrubka_mm?: string | number | null;
  x_mm?: number | null;
  y_mm?: number | null;
}): string {
  if (m.z_mm != null && m.z_mm !== "") return `${cisloMm(m.z_mm)} mm`;
  if (m.sirka_mm != null && m.hrubka_mm != null) return rozmerHrany(m.sirka_mm, m.hrubka_mm);
  if (m.x_mm && m.y_mm) return `${m.x_mm}×${m.y_mm}`;
  return "—";
}

/** Nákupná cena za MJ — `0,28 €/m`, `9,40 €/m2`. */
export function cenaZaMj(
  cena: string | number | null | undefined,
  mj: string | null | undefined,
): string {
  const suma = formatEur(cena);
  return mj ? `${suma}/${mj}` : suma;
}

/**
 * Vstup s desatinnou čiarkou → bodka (SK zvyk: „1,42" → „1.42").
 * Používaj pred odoslaním číselného stringu do API (backend čaká
 * Decimal s bodkou) aj pred `parseFloat` v živých výpočtoch.
 */
export function parseDecimal(v: string | number | null | undefined): string {
  return String(v ?? "").replace(/,/g, ".").trim();
}

/** `parseFloat` tolerantný voči čiarke — vráti NaN pri neplatnom vstupe. */
export function toNum(v: string | number | null | undefined): number {
  return parseFloat(parseDecimal(v));
}

/** "500.00" → "500", "22.5" → "22.5", null → "" — čísla bez zbytočných núl. */
export function normalizeNum(v: string | number | null | undefined): string {
  if (v == null || v === "") return "";
  const n = Number(v);
  return Number.isNaN(n) ? String(v) : String(n);
}

export function formatDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  return `${dd}.${mm}.${d.getFullYear()}`;
}

/** Minimálny tvar klienta pre zobrazenie mena (Klient aj KlientSummary). */
interface KlientNameLike {
  id: string;
  firma?: string | null;
  meno?: string | null;
  priezvisko?: string | null;
}

/** Zobraziteľné meno klienta: firma → meno priezvisko → skrátené ID. */
export function formatKlientName(k: KlientNameLike): string {
  return (
    k.firma ||
    `${k.meno ?? ""} ${k.priezvisko ?? ""}`.trim() ||
    k.id.slice(0, 8)
  );
}

/** Minimálny tvar materiálu s ID stĺpcami dodávateľov. */
interface MaterialIdLike {
  daex_id?: string | null;
  hranipex_id?: string | null;
  jafholz_id?: string | null;
  twd_id?: string | null;
  hafele_id?: string | null;
  demos_id?: string | null;
  sklo_mier_id?: string | null;
  nabytkar_id?: string | null;
  /** Kód tovaru (KOLO 230) — CRC, ktorým sa objednáva */
  tovar_kod?: string | null;
  vlastny_id?: string | null;
}

/** Primárne ID materiálu — prvý vyplnený dodávateľský stĺpec. */
export function materialPrimaryId(m: MaterialIdLike): string | null {
  return (
    m.daex_id ??
    m.hranipex_id ??
    m.jafholz_id ??
    m.twd_id ??
    m.hafele_id ??
    m.demos_id ??
    m.sklo_mier_id ??
    // poradie ako `ID_POLIA` v backende (`material_kod`) — bez týchto
    // dvoch by tabuľka ukázala iný kód než Cut Rite (KOLO 137)
    m.nabytkar_id ??
    m.tovar_kod ??
    m.vlastny_id ??
    null
  );
}

export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const date = formatDate(iso);
  const hh = String(d.getHours()).padStart(2, "0");
  const mi = String(d.getMinutes()).padStart(2, "0");
  return `${date} ${hh}:${mi}`;
}

/**
 * Kedy sa zákazka naposledy menila — tak, ako sa o tom hovorí („pred 3 h“,
 * „včera“), starší dátum krátko „3. 10.“, z iného roka „3. 10. 2025“.
 * Presný čas patrí do tooltipu (`formatDateTime`). Prehľad zákaziek
 * ukazoval pri každej zákazke len `12.10.2026` (KOLO 171).
 *
 * `now` je parameter kvôli testom — pevný čas bez falošných hodín.
 */
export function formatUpravene(
  iso: string | null | undefined,
  now: Date = new Date(),
): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  const sekundy = (now.getTime() - d.getTime()) / 1000;
  // Čas mierne v budúcnosti (hodiny servera) nie je „pred -2 min“
  if (sekundy < 60) return "práve teraz";
  if (sekundy < 3600) return `pred ${Math.floor(sekundy / 60)} min`;
  const dni = kalendarnychDni(d, now);
  if (dni === 0) return `pred ${Math.floor(sekundy / 3600)} h`;
  if (dni === 1) return "včera";
  if (dni < 7) return `pred ${dni} dňami`;
  const datum = `${d.getDate()}. ${d.getMonth() + 1}.`;
  return d.getFullYear() === now.getFullYear()
    ? datum
    : `${datum} ${d.getFullYear()}`;
}

/** Počet kalendárnych dní medzi dvoma okamihmi v miestnom čase. */
function kalendarnychDni(od: Date, po: Date): number {
  const a = new Date(od.getFullYear(), od.getMonth(), od.getDate());
  const b = new Date(po.getFullYear(), po.getMonth(), po.getDate());
  // round, nie floor — deň so zmenou času má 23 alebo 25 hodín
  return Math.round((b.getTime() - a.getTime()) / 86_400_000);
}

/**
 * Rozmery dielca v poradí PODĽA SMERU VLÁKIEN — na zobrazenie.
 *
 * Smer „Y" znamená vlákno po šírke → dielec sa na tabuli (aj v poreze)
 * reže otočený o 90°, preto UI ukazuje `(y, x)` (500×300 → 300×500).
 * Uložené `x_mm`/`y_mm` sa NEMENIA — je to čisto zobrazovacie poradie.
 */
export function zobrazRozmer<T>(x: T, y: T, smer: string | null | undefined): [T, T] {
  return smer === "Y" ? [y, x] : [x, y];
}

/** Čitateľná merná jednotka cenníka (`m2` → `m²`); neznáma ostane, ako je. */
const MJ_NAZVY: Record<string, string> = {
  m2: "m²",
  m: "m",
  bm: "bm",
  ks: "ks",
  set: "set",
  sad: "sada",
  par: "pár",
  kg: "kg",
  l: "l",
  kt: "kart.",
  pk: "bal.",
  bal: "bal.",
  arch: "hárok",
};

export function formatMj(mj: string | null | undefined): string {
  if (!mj) return "—";
  return MJ_NAZVY[mj.toLowerCase()] ?? mj;
}

/**
 * Slovenský tvar podstatného mena podľa počtu: 1 materiál, 2–4 materiály,
 * 0 a 5+ materiálov. Desatinné a záporné čísla idú ako „5+".
 */
export function sklonuj(
  n: number,
  [jeden, dvaStyri, pat]: readonly [string, string, string],
): string {
  if (n === 1) return jeden;
  if (n >= 2 && n <= 4 && Number.isInteger(n)) return dvaStyri;
  return pat;
}

/** Počet s medzerou po tisícoch ako v PDF („35 120"). */
export function formatPocet(n: number): string {
  return n.toLocaleString("sk-SK");
}

/**
 * Percento po slovensky: „20 %", „12,5 %" (nezlomiteľná medzera).
 * Réžia v Cenníku bola „20.00%" — surový text z API (KOLO 166).
 * `desatinne` = pevný počet desatín („14,8 %" aj pri 15) — porez a Cut
 * Rite ich písali cez `toFixed` s bodkou (KOLO 218).
 */
export function formatPct(
  value: string | number | null | undefined,
  desatinne?: number,
): string {
  if (value === null || value === undefined || value === "") return "—";
  const num = typeof value === "number" ? value : Number(value);
  if (Number.isNaN(num)) return "—";
  const tvar =
    desatinne === undefined
      ? { maximumFractionDigits: 2 }
      : { minimumFractionDigits: desatinne, maximumFractionDigits: desatinne };
  return `${num.toLocaleString("sk-SK", tvar)}\u00a0%`;
}

/** Plocha v m² na dve desatiny po slovensky („12,45"), bez jednotky. */
export function formatM2(value: string | number | null | undefined): string {
  if (value === null || value === undefined || value === "") return "—";
  const num = toNum(value);
  if (!Number.isFinite(num)) return "—";
  return num.toLocaleString("sk-SK", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

/**
 * Meno kovania na obrazovke (KOLO 176) — alias hlavný, pravý názov pod
 * ním. User: *„alias dávaš úplne dozadu … urob to naopak, že hlavný nápis
 * bude alias a pod ním ten jeho pravý"*. Bez aliasu je hlavný názov
 * a `pod` je `null`. Objednávky a súpis pre dodávateľa ostávajú
 * s pravým názvom — alias dodávateľ nepozná.
 */
export function menoKovania(m: { nazov: string; alias?: string | null }): {
  hlavne: string;
  pod: string | null;
} {
  const alias = m.alias?.trim();
  return alias ? { hlavne: alias, pod: m.nazov } : { hlavne: m.nazov, pod: null };
}

/** Zostava, ktorá sa môže predávať na metre (KOLO 227). */
interface ZostavaJednotka {
  jednotka?: string | null;
  mnozstvo_m?: string | number | null;
  dlzka_kusa_mm?: string | number | null;
  pocet_ks?: number | null;
}

/**
 * Zostava sa predáva na metre (KOLO 227) — to isté pravidlo ako
 * `calculator.je_v_metroch`: jednotka `m`, metre aj dĺžka 1 ks.
 */
export function vMetroch(z: ZostavaJednotka): boolean {
  return (
    z.jednotka === "m" &&
    z.mnozstvo_m != null &&
    z.mnozstvo_m !== "" &&
    Number(z.dlzka_kusa_mm ?? 0) > 0
  );
}

/** Číslo po slovensky bez zbytočných núl: 1.500 → „1,5", 2 → „2". */
export function formatCisloBezNul(v: string | number | null | undefined): string {
  return normalizeNum(v).replace(".", ",");
}

/** „1,5 m" v metroch, inak „2 ks" (KOLO 227). */
export function formatMnozstvoZostavy(z: ZostavaJednotka): string {
  if (vMetroch(z)) return `${formatCisloBezNul(z.mnozstvo_m)} m`;
  return `${z.pocet_ks ?? 1} ks`;
}

/**
 * Kusy pre výrobu z metrov — nahor, aspoň 1 (`calculator.pocet_z_metrov`).
 * Len na náhľad v okne; skutočné `pocet_ks` drží server.
 */
export function kusyZMetrov(metre: number, dlzkaMm: number): number {
  if (!(metre > 0) || !(dlzkaMm > 0)) return 1;
  // Zaokrúhlenie na 6 miest — 1,2 / 0,6 v plávajúcej čiarke nie je presne 2
  const kusy = Math.round(((metre * 1000) / dlzkaMm) * 1e6) / 1e6;
  return Math.max(1, Math.ceil(kusy));
}
